import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams, Link } from "react-router-dom";

import { CAP, useSession } from "@/auth/session";
import { assetCatalog, organisations, products } from "@/api/client";
import { ImageField } from "@/components/ImageCapture";
import { SearchInput, Select } from "@/components/inputs";
import { useProducts } from "@/modules/products/data";
import { ScopeAction, ScopeField } from "@/modules/products/ScopeBar";

import { Chip } from "@/components/Chip";
import { Drawer } from "@/components/Drawer";
import { DataTable, type DataCol } from "@/components/DataTable";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { StatGrid } from "@/components/StatGrid";
import { ErrorNote } from "@/components/ErrorNote";
import { Icon } from "@/components/icons";
import { PersonDrawer } from "@/modules/organisations/PersonDrawer";
import {
  ROLE_LABEL, orgQk, useManufacturerFacet, useOrgUsers, useOrganisation,
  type AllocationRow, type OrgUserRow,
} from "@/modules/organisations/data";

/** One organisation: what it holds and who works in it.
 *
 *  REAL DATA. This page rendered `detail_fixtures.ts`, which is where "Nexcell
 *  line 2" came from — a space in a company that does not exist, allocated to
 *  a product ("Warranty Insights") that does not exist either.
 *
 *  AN ALLOCATION is `(company, product, space)`: the thing a person is
 *  actually granted, and the thing a tool enters. The product is a catalogue
 *  KIND since D-92, and the space belongs to that kind (D-89) — so what
 *  differs between companies is never WHICH spaces exist, only which they
 *  hold and what may be done in them.
 *
 *  404 MEANS BOTH "no such organisation" AND "not yours", deliberately. A
 *  distinct answer would tell a customer that CMP-014 is real.
 */
function allocCols(mayAuthor: boolean,
                   toggle: (a: AllocationRow) => void,
                   busy: boolean,
                   open: (a: AllocationRow) => void): DataCol<AllocationRow>[] {
  return [
    {
      key: "kind", label: "Product", width: 180, mono: true,
      render: (a) => (
        <span style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
          {a.kind}
          {/* An allocation OUTLIVES its entitlement on purpose — deleting it
              cascades who may enter the space and every override. Without
              this chip the row reads as access that grants something. */}
          {!a.holds_product && <Chip tone="warn">product not held</Chip>}
        </span>
      ),
    },
    { key: "space", label: "Space", width: 180, render: (a) => a.custom_name ?? a.space },
    { key: "slug", label: "Slug", width: 160, mono: true, render: (a) => a.space_slug },
    {
      key: "users", label: "People with access", width: 150,
      render: (a) => a.users_with_access,
    },
    {
      // The answer nobody could get before (D-102): three tables held the
      // chain and nothing computed it, so "allocated" was as much as this
      // page could say.
      key: "grants", label: "Grants", width: 130,
      render: (a) => (
        <button className="btn ghost" onClick={() => open(a)}>What it grants</button>
      ),
    },
    {
      key: "enabled", label: "State", width: 110,
      render: (a) => a.enabled
        ? <Chip tone="ok">enabled</Chip>
        : <Chip tone="mute">disabled</Chip>,
    },
    ...(mayAuthor ? [{
      key: "act", label: "", width: 110,
      render: (a: AllocationRow) => (
        <button className="btn ghost" disabled={busy}
          // DISABLE, not remove. The control says so, because the two look
          // the same from here and only one of them is reversible.
          title={a.enabled
            ? "Stop this organisation using the space. Access and overrides are kept."
            : "Let this organisation use the space again."}
          onClick={() => toggle(a)}>
          {a.enabled ? "Disable" : "Enable"}
        </button>
      ),
    }] : []),
  ];
}

const USER_COLS: DataCol<OrgUserRow>[] = [
  { key: "name", label: "Name", width: 170, render: (u) => u.name },
  { key: "login", label: "Login", width: 230, mono: true, render: (u) => u.login_id },
  { key: "role", label: "Role", width: 140, render: (u) => ROLE_LABEL[u.role] ?? u.role },
  {
    key: "mfa", label: "MFA", width: 80,
    render: (u) => u.mfa ? <Chip tone="ok">on</Chip> : <Chip tone="warn">off</Chip>,
  },
  {
    key: "access", label: "Space access", width: 220,
    render: (u) => u.space_access.length
      ? u.space_access.map((a) => `${a.kind}/${a.space}`).join(", ")
      : <span className="cap">none</span>,
  },
];

export function OrganisationDetailPage() {
  const { orgId = "" } = useParams();
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useOrganisation(orgId);
  const people = useOrgUsers(orgId);

  const [grants, setGrants] = useState<AllocationRow | null>(null);
  const [person, setPerson] = useState<OrgUserRow | null>(null);

  const toggle = useMutation({
    mutationFn: (a: AllocationRow) => products.setAllocation(a.cps_id, !a.enabled),
    onSuccess: () => void qc.invalidateQueries({ queryKey: orgQk.detail(orgId) }),
  });

  if (q.isLoading) {
    return <div className="mpage on"><div className="panel panel-pad cap">Loading…</div></div>;
  }
  if (q.isError) {
    return (
      <div className="mpage on">
        <div className="panel panel-pad">
          No such organisation, or it is not one you may see.{" "}
          <Link to="/organisations/organisations">Back to organisations</Link>
        </div>
      </div>
    );
  }

  const o = q.data!;
  const users = people.data?.users ?? [];

  return (
    <div className="mpage on">
      {/* THE HOUSE RHYTHM, taken from the pages that already had it — Parts
          Inventory, Drivers, Asset Catalog: PageHead, StatGrid, then
          `section-label` + panel for each section, first one `flush`. This
          page was written quickly and showed it: `<h3 className="cap">` for
          headings where every other page uses `.section-label` (a different
          size, weight and colour), and `KV` rows laid out in a `flexWrap`,
          which breaks them — KV is a full-width `space-between` row with a
          rule underneath, so wrapped they shrink to their content and nothing
          lines up. */}
      <PageHead
        eyebrow="Organisations"
        title={o.name}
        desc="A party in the registry: it can own, hold, ship and receive. Being a manufacturer is a FACET of this row, not a second company."
      />

      <StatGrid stats={[
        { k: "Allocations", v: o.allocations.length },
        { k: "People", v: users.length },
        { k: "With MFA", v: users.filter((u) => u.mfa).length },
      ]} />

      <div className="section-label flush">Identity</div>
      <div className="panel panel-pad">
        <KV k="Organisation ID" v={o.organisation_id} mono />
        <KV k="Plan" v={o.plan} mono={false} />
        <KV k="Status" v={o.status} mono={false} />
      </div>

      <ManufacturerFacet cid={o.organisation_id} />

      {/* INHERITED, not allocated one at a time (D-102). A company granted a
          product gets every space of it, so there is no "add space" control
          here — the way to give one is to grant the product, on Products →
          Product visibility. What this table offers is the narrowing. */}
      <div className="section-label">Allocated spaces</div>
      <ErrorNote error={toggle.error} />
      <DataTable rows={o.allocations}
        cols={allocCols(can(CAP.MANAGE_PRODUCTS), (a) => toggle.mutate(a),
          toggle.isPending, setGrants)}
        rowKey={(a) => a.cps_id}
        empty={<div className="panel panel-pad cap">
          This organisation holds no product yet, so it has no spaces. Grant one
          on Products → Product visibility.
        </div>} />

      {grants && <GrantsDrawer alloc={grants} onClose={() => setGrants(null)} />}

      <div className="section-label">People</div>
      {/* THE ROW OPENS THE PERSON. Every admin action needs this row's
          context to be safe — what the refusals say depends on their role,
          their organisation and whether they have ever signed in — so the
          actions live in a drawer over the list rather than as columns. */}
      <DataTable rows={users} cols={USER_COLS} rowKey={(u) => u.user_id}
        onRowClick={can(CAP.MANAGE_ORGANISATIONS) ? setPerson : undefined}
        empty={<div className="panel panel-pad cap">
          {people.isLoading ? "Loading…" : "Nobody in this organisation yet."}
        </div>} />

      {person && <PersonDrawer user={person} onClose={() => setPerson(null)} />}
    </div>
  );
}


/** What this allocated space ACTUALLY grants, and why (D-102).
 *
 *  THE CHAIN IS SHOWN, not just its result. `from` says which layer decided
 *  each permission — the space default, this organisation's override, or the
 *  person's — because "why can this bench not flash" is the question that
 *  actually gets asked, and a flat list of numbers cannot answer it.
 *
 *  THE TWO GATES ARE SHOWN SEPARATELY. Entitlement and the enabled switch
 *  zero everything without changing a single permission value, so a screen
 *  that only rendered the result would show a space granting nothing with no
 *  indication that the cause was one row on another page.
 */
function GrantsDrawer({ alloc, onClose }: { alloc: AllocationRow; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["allocation", alloc.cps_id, "permissions"],
    queryFn: () => products.effectivePermissions(alloc.cps_id),
    staleTime: 60_000, refetchOnWindowFocus: false,
  });
  const d = q.data;

  return (
    <Drawer onClose={onClose} header={
      <span style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
        <strong>{alloc.custom_name ?? alloc.space}</strong>
        <span className="mono cap">{alloc.kind}</span>
      </span>
    }>
      {q.isLoading && <div className="cap">Loading…</div>}
      {d?.blocked_because && (
        <div className="panel panel-pad" style={{
          borderColor: "var(--warn)",
          background: "color-mix(in srgb, var(--warn) 8%, transparent)",
          marginBottom: 12,
        }}>
          <strong>Nothing is granted here.</strong> {d.blocked_because}.
        </div>
      )}
      {d && !d.permissions.length && (
        <div className="panel panel-pad cap">
          This space grants no permissions yet. A super admin fixes them on
          Products → Permission map.
        </div>
      )}
      {d && d.permissions.length > 0 && (
        <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
          <thead>
            <tr className="cap">
              <th style={{ textAlign: "left", width: 70 }}>No.</th>
              <th style={{ textAlign: "left" }}>Permission</th>
              <th style={{ textAlign: "left", width: 110 }}>Decided by</th>
              <th style={{ textAlign: "left", width: 90 }}>State</th>
            </tr>
          </thead>
          <tbody>
            {d.permissions.map((p) => (
              <tr key={p.perm_uid} style={{ borderTop: "1px solid var(--line)" }}>
                <td className="mono">{p.permission_id}</td>
                <td style={{ textDecoration: p.deprecated ? "line-through" : undefined }}>
                  {p.name}
                </td>
                <td className="cap">{p.from}</td>
                <td>
                  {p.effective
                    ? <Chip tone="ok">granted</Chip>
                    : <Chip tone="mute">{p.granted ? "blocked" : "off"}</Chip>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Drawer>
  );
}


/** The MAKER facet of this organisation (D-80, D-93).
 *
 *  An organisation IS a party. `manufacturers` holds only what is specific
 *  to MAKING things — abbreviation, what it supplies, country — and D-93
 *  retires the separate Manufacturers sub-page because one organisation
 *  edited in two places is how the live data ended up with "Aphelion Energy
 *  Pvt Ltd" in one table and "Aphelion Energy" in the other.
 *
 *  THERE IS NO NAME FIELD HERE, deliberately. The name comes from the party
 *  above. Offering one would recreate exactly the split this removes.
 *
 *  `supplies` GATES WHICH MODELS this maker may appear on, which is why it
 *  is a checklist of real products rather than free text: a typo produces a
 *  manufacturer that silently never shows up in the picker it was made for,
 *  and that reads as a broken picker.
 */
function ManufacturerFacet({ cid }: { cid: string }) {
  const { can } = useSession();
  // TWO capabilities, because this panel spans two modules. Editing the
  // facet's own fields is MANAGE_ORGANISATIONS; the logo and the archive
  // are CATALOGUE writes — they call `assetCatalog.*`, which is gated on
  // MANAGE_CATALOG. Checking only the first would offer buttons that 403 to
  // anyone holding one and not the other. Both are super-admin-only today,
  // so nobody would hit it — which is exactly why it needed a test rather
  // than a reviewer.
  const mayAuthor = can(CAP.MANAGE_ORGANISATIONS);
  const mayCatalogue = can(CAP.MANAGE_CATALOG);
  const q = useManufacturerFacet(cid);
  const prods = useProducts();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [abbr, setAbbr] = useState("");
  const [country, setCountry] = useState("");
  const [supplies, setSupplies] = useState<string[]>([]);

  const facet = q.data;
  const isMaker = !!facet?.manufacturer_id;

  const open = () => {
    setAbbr(facet?.abbr ?? "");
    setCountry(facet?.country ?? "");
    setSupplies(facet?.supplies ?? []);
    setEditing(true);
  };

  const archive = useMutation({
    mutationFn: () => assetCatalog.archiveManufacturer(facet!.manufacturer_id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgQk.manufacturer(cid) });
      void qc.invalidateQueries({ queryKey: orgQk.list });
    },
  });

  const save = useMutation({
    mutationFn: () => organisations.setManufacturer(cid, {
      abbr: abbr.trim() || null, country: country.trim() || null, supplies,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgQk.manufacturer(cid) });
      void qc.invalidateQueries({ queryKey: orgQk.list });
      setEditing(false);
    },
  });

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div className="section-label">Manufacturer</div>
        {isMaker
          ? <Chip tone="info">{facet?.models ?? 0} model{facet?.models === 1 ? "" : "s"}</Chip>
          : <Chip tone="mute">not a manufacturer</Chip>}
        {mayAuthor && !editing && (
          <button className="btn ghost" style={{ marginLeft: "auto" }} onClick={open}>
            {isMaker ? "Edit maker details" : "Make this a manufacturer"}
          </button>
        )}
      </div>

      {!editing && isMaker && (
        <div className="panel panel-pad">
          {facet?.logo_at && (
            <img src={assetCatalog.logoUrl(facet.manufacturer_id!, facet.logo_at)}
              alt="" style={{ width: 48, height: 48, objectFit: "contain",
                borderRadius: 8, border: "1px solid var(--line2)",
                marginBottom: 10 }} />
          )}
          <KV k="Abbreviation" v={facet?.abbr ?? "—"} mono />
          <KV k="Country" v={facet?.country ?? "—"} mono={false} />
          <KV k="Supplies" v={facet?.supplies.length
            ? facet.supplies.join(", ") : "nothing yet"} mono={false} />
          {mayCatalogue && (
            <div style={{ display: "flex", gap: 8, alignItems: "center",
              marginTop: 12, flexWrap: "wrap" }}>
              {/* ImageField (U-shared) — the same control the catalogue's
                  own manufacturer form uses, pointed at the same endpoints.
                  Hand-rolling a second uploader here would be the review
                  failure CLAUDE.md names, and a worse one than the original. */}
              <ImageField camera={false} size={64} fit="contain" empty="no logo"
                src={facet?.logo_at
                  ? assetCatalog.logoUrl(facet.manufacturer_id!, facet.logo_at)
                  : null}
                onUpload={async (b) => {
                  await assetCatalog.uploadLogo(facet!.manufacturer_id!, b);
                  await qc.invalidateQueries({ queryKey: orgQk.manufacturer(cid) });
                }}
                onRemove={async () => {
                  await assetCatalog.deleteLogo(facet!.manufacturer_id!);
                  await qc.invalidateQueries({ queryKey: orgQk.manufacturer(cid) });
                }} />
              {/* Archive is REFUSED while the maker still has models — the
                  server says so ("has N models — retire those first"), and
                  that refusal is the point: archiving a maker whose models
                  are still buildable would hide it from the picker those
                  models need. */}
              <button className="btn ghost" disabled={(facet?.models ?? 0) > 0
                || archive.isPending}
                title={(facet?.models ?? 0) > 0
                  ? "retire its models first" : "hide it from pickers"}
                onClick={() => archive.mutate()}>
                Archive maker
              </button>
            </div>
          )}
        </div>
      )}

      {!editing && !isMaker && (
        <div className="panel panel-pad cap">
          This organisation does not make anything. A manufacturer is a facet
          of a party (D-80) — the same row, with maker-only fields — so it is
          added here rather than created as a second company.
        </div>
      )}

      {editing && (
        <div className="panel panel-pad" style={{ display: "flex",
          gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <ScopeField label="Abbreviation"
            hint="prefixes technical model names — unique across makers">
            <SearchInput value={abbr} mono placeholder="AE" style={{ width: 90 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAbbr(e.target.value.toUpperCase())} />
          </ScopeField>
          <ScopeField label="Country" hint="two letters">
            <SearchInput value={country} mono placeholder="IN" style={{ width: 70 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCountry(e.target.value.toUpperCase().slice(0, 2))} />
          </ScopeField>
          <ScopeField label="Supplies"
            hint="which products it may appear on — a wrong one hides it from that picker">
            <Select value="" style={{ minWidth: 220 }}
              onChange={(e) => {
                const k = e.target.value;
                if (k && !supplies.includes(k)) setSupplies([...supplies, k]);
              }}>
              <option value="">Add a product…</option>
              {(prods.data?.products ?? [])
                .filter((p) => !supplies.includes(p.kind))
                .map((p) => <option key={p.kind} value={p.kind}>{p.name}</option>)}
            </Select>
          </ScopeField>
          <div style={{ flexBasis: "100%", display: "flex", gap: 6, flexWrap: "wrap" }}>
            {supplies.map((k) => (
              <button key={k} className="btn ghost"
                onClick={() => setSupplies(supplies.filter((x) => x !== k))}>
                {k} <Icon name="close" />
              </button>
            ))}
            {supplies.length === 0 && (
              <span className="cap">
                Supplies nothing yet — it will not appear on any model picker.
              </span>
            )}
          </div>
          <ScopeAction>
            <span style={{ display: "flex", gap: 8 }}>
              <button className="btn ghost" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button className="btn teal" disabled={save.isPending}
                onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
            </span>
          </ScopeAction>
          <ErrorNote error={save.error} />
        </div>
      )}
    </>
  );
}
